// src/pages/admin/ManageCompetitions.tsx
import React, { useState, useEffect, useRef } from "react";
import { Timestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import {
  getAllCompetitions,
  addCompetition,
  updateCompetition,
  updateCompetitionStatus,
  updateCompetitionImage,
  updateCompetitionDocument,
  removeCompetitionDocument,
  updateCompetitionDocument2,
  removeCompetitionDocument2,
  deleteCompetition,
  propagateCompetitionRename,
} from "../../firebase/competitions";
import {
  addRankingCompetition,
  getRankingCompetitionByLinkedId,
  getRankingCompetitions,
  updateRankingCompetition,
} from "../../firebase/rankings";
import { WEIGHT_CLASSES_BY_GROUP, COMPETITION_TYPE_LABELS, getDefaultPointsForType } from "../../utils/rankingCalculator";
import { useTranslation } from "../../hooks/useTranslation";
import { formatDateGeorgian } from "../../utils/dateHelpers";
import ManualRegistrationModal from "../../components/ManualRegistrationModal";
import { useAuth } from "../../context/AuthContext";
import { C, FONT } from "../../theme";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}


// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  name: "",
  startDate: "",
  endDate: "",
  registrationDeadline: "",
  earlyDeadline: "",
  midDeadline: "",
  weightChangeDeadline: "",
  earlyPrice: "",
  midPrice: "",
  latePrice: "",
  status: "open",
  rankingType: "custom" as string,
  rankingTotalPoints: 0 as number,
  rankingParticipationPoints: 1 as number,
  rankingPointsPerWin: 0 as number,
};

const TYPE_OPTIONS = ["Kids A", "Kids B", "Cadets", "Juniors", "U21", "Seniors"];
const TYPE_TO_WEIGHT_GROUP: Record<string, string> = {
  "Kids A":  "Kids",
  "Kids B":  "Fin",
  "Cadets":  "Cadets",
  "Juniors": "Juniors",
  "U21":     "U21",
  "Seniors": "Seniors",
};

// ---------------------------------------------------------------------------
// Delete confirmation modal
// ---------------------------------------------------------------------------

interface DeleteModalProps {
  comp: any;
  regCount: number;
  checking: boolean;
  deleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  t: (key: string) => string;
}

function DeleteModal({
  comp,
  regCount,
  checking,
  deleting,
  onConfirm,
  onCancel,
  t,
}: DeleteModalProps) {
  return (
    <div style={dm.overlay}>
      <div style={dm.modal}>
        <div style={dm.accentBar} />
        <div style={dm.body}>
          <div style={dm.title}>{t("admin.competitions.delete.confirm.title")}</div>
          <div style={dm.compName}>{comp.name}</div>
          <div style={dm.msg}>{t("admin.competitions.delete.confirm.msg")}</div>

          {checking && <div style={dm.checking}>Checking registrations...</div>}

          {!checking && regCount > 0 && (
            <div style={dm.warning}>
              {t("admin.competitions.delete.confirm.hasRegs")}{" "}
              <strong>({regCount} registrations)</strong>
            </div>
          )}

          <div style={dm.actions}>
            <button style={dm.cancelBtn} onClick={onCancel} disabled={deleting}>
              {t("admin.competitions.delete.confirm.cancel")}
            </button>
            <button
              style={deleting ? dm.confirmBtnDisabled : dm.confirmBtn}
              onClick={onConfirm}
              disabled={deleting || checking}
            >
              {deleting ? "Deleting..." : t("admin.competitions.delete.confirm.yes")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const ManageCompetitions = () => {
  const { t } = useTranslation();
  const locale = localStorage.getItem("language") || "en";
  const navigate = useNavigate();
  const w = useWindowWidth();
  const isMobile = w < 768;

  // ── Role gate ───────────────────────────────────────────────────────────
  // ranking_admin must SEE everything but cannot delete, toggle open/close,
  // or fire manual registrations. Buttons that trigger those actions are
  // hidden when isRankingAdmin === true.
  const { userRole } = useAuth();
  const isRankingAdmin = userRole === "ranking_admin";

  // ── Per-card registration counts ─────────────────────────────────────────
  // Read directly off competition.registrationCount, which the
  // maintainCompetitionCount Cloud Function keeps in sync on every
  // registration write. No additional Firestore reads are needed.

  const [competitions, setCompetitions] = useState<any[]>([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingCompId, setEditingCompId] = useState<string | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [createImageFile, setCreateImageFile] = useState<File | null>(null);
  const [createImagePreview, setCreateImagePreview] = useState<string>("");
  const [maleWeights, setMaleWeights] = useState<string[]>([]);
  const [femaleWeights, setFemaleWeights] = useState<string[]>([]);
  const [maleInput, setMaleInput] = useState("");
  const [femaleInput, setFemaleInput] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [focusedField, setFocusedField] = useState("");

  const [activeTab, setActiveTab] = useState<"open" | "closed">("open");
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    comp: any | null;
    regCount: number;
    checking: boolean;
    deleting: boolean;
  }>({ open: false, comp: null, regCount: 0, checking: false, deleting: false });
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [uploadingForComp, setUploadingForComp] = useState<string | null>(null);
  // Official-document (PDF) upload — separate from the image so the two flows
  // never collide. Available to BOTH admin and ranking_admin.
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const [docForComp, setDocForComp] = useState<string | null>(null);
  const [uploadingDoc2, setUploadingDoc2] = useState<string | null>(null);
  const [doc2ForComp, setDoc2ForComp] = useState<string | null>(null);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [manualRegComp, setManualRegComp] = useState<any | null>(null);
  const [searchText, setSearchText] = useState("");
  const [pastPage, setPastPage] = useState(1);

  // Propagation state
  const [originalCompData, setOriginalCompData] = useState<any>(null);
  const [propagatePending, setPropagationPending] = useState<{
    competitionId: string;
    updates: { newName?: string; newNameKa?: string; newStartDate?: Date };
    yearChanged: boolean;
  } | null>(null);
  const [propagating, setPropagating] = useState(false);
  const [propagateResult, setPropagateResult] = useState<{
    rankingResultsUpdated: number;
    registrationsUpdated: number;
    rankingCompetitionUpdated: boolean;
  } | null>(null);
  const [propagateError, setPropagateError] = useState("");

  // Link-to-rankings state
  // linkingCompId: the comp.id currently being linked (shows "Linking..." on that button)
  // linkFeedback: per-comp success or error message shown inline under the button
  const [linkingCompId, setLinkingCompId] = useState<string | null>(null);
  const [linkFeedback, setLinkFeedback] = useState<
    Record<string, { success?: string; error?: string }>
  >({});

  const imageInputRef = useRef<HTMLInputElement>(null);
  const createImageInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const doc2InputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCompetitions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCompetitions = async () => {
    setLoading(true);
    try {
      const data = await getAllCompetitions();
      setCompetitions(data);
    } catch {
      setError(t("admin.competitions.error.load"));
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Form handlers
  // ---------------------------------------------------------------------------

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleTypeToggle = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleCreateImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCreateImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setCreateImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const addWeight = (gender: string) => {
    if (gender === "male") {
      const val = maleInput.trim();
      if (val && !maleWeights.includes(val)) setMaleWeights([...maleWeights, val]);
      setMaleInput("");
    } else {
      const val = femaleInput.trim();
      if (val && !femaleWeights.includes(val))
        setFemaleWeights([...femaleWeights, val]);
      setFemaleInput("");
    }
  };

  const removeWeight = (gender: string, weight: string) => {
    if (gender === "male")
      setMaleWeights(maleWeights.filter((w) => w !== weight));
    else setFemaleWeights(femaleWeights.filter((w) => w !== weight));
  };

  const loadDefaultWeights = () => {
    if (selectedTypes.length === 0) return;

    const sortWeights = (a: string, b: string) => {
      const isPlus = (s: string) => s.startsWith("+");
      if (isPlus(a) && !isPlus(b)) return 1;
      if (!isPlus(a) && isPlus(b)) return -1;
      return parseInt(a.replace(/[^0-9]/g, ""), 10) - parseInt(b.replace(/[^0-9]/g, ""), 10);
    };

    const male = new Set<string>();
    const female = new Set<string>();

    selectedTypes.forEach((type) => {
      const groupKey = TYPE_TO_WEIGHT_GROUP[type];
      if (!groupKey) return;
      const group = WEIGHT_CLASSES_BY_GROUP[groupKey];
      if (!group) return;
      group.male.forEach((w) => male.add(w));
      group.female.forEach((w) => female.add(w));
    });

    setMaleWeights([...male].sort(sortWeights));
    setFemaleWeights([...female].sort(sortWeights));
  };

  // ---------------------------------------------------------------------------
  // Edit helper — converts Firestore Timestamps back to "yyyy-MM-dd" strings
  // ---------------------------------------------------------------------------

  const tsToDateStr = (ts: any): string => {
    if (!ts) return "";
    if (typeof ts?.toDate === "function") return ts.toDate().toISOString().split("T")[0];
    if (ts instanceof Date) return ts.toISOString().split("T")[0];
    return "";
  };

  const handleEditClick = (comp: any) => {
    setEditingCompId(comp.id);
    setOriginalCompData(comp);
    setForm({
      name: comp.name || "",
      startDate: tsToDateStr(comp.startDate),
      endDate: tsToDateStr(comp.endDate),
      registrationDeadline: tsToDateStr(comp.registrationDeadline),
      earlyDeadline: tsToDateStr(comp.earlyDeadline),
      midDeadline: tsToDateStr(comp.midDeadline),
      weightChangeDeadline: tsToDateStr(comp.weightChangeDeadline),
      earlyPrice: String(comp.earlyPrice ?? ""),
      midPrice: String(comp.midPrice ?? ""),
      latePrice: String(comp.latePrice ?? ""),
      status: comp.status || "open",
      rankingType: comp.rankingType || "custom",
      rankingTotalPoints: comp.rankingTotalPoints ?? 0,
      rankingParticipationPoints: comp.rankingParticipationPoints ?? 1,
      rankingPointsPerWin: comp.rankingPointsPerWin ?? 0,
    });
    setSelectedTypes(comp.types ?? []);
    setMaleWeights(comp.weightCategories?.male ?? []);
    setFemaleWeights(comp.weightCategories?.female ?? []);
    setCreateImageFile(null);
    setCreateImagePreview(comp.imageUrl || "");
    setError("");
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---------------------------------------------------------------------------
  // Submit — creates OR updates the competition
  // ---------------------------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Only enforce types if the competition never had any types defined.
    // This allows editing dates/name of old competitions without forcing a type selection.
    const originalHadTypes = editingCompId && originalCompData?.types && originalCompData.types.length > 0;
    if (selectedTypes.length === 0 && (!editingCompId || originalHadTypes)) {
      setError(t("admin.competitions.error.noTypes"));
      return;
    }
    if (maleWeights.length === 0 && femaleWeights.length === 0) {
      setError(t("admin.competitions.error.noWeights"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const compPayload = {
        ...form,
        types: selectedTypes,
        earlyPrice: Number(form.earlyPrice),
        midPrice: Number(form.midPrice),
        latePrice: Number(form.latePrice),
        weightCategories: { male: maleWeights, female: femaleWeights },
      };

      if (editingCompId) {
        // ── UPDATE existing competition ──────────────────────────────────────
        await updateCompetition(editingCompId, compPayload, createImageFile ?? undefined);

        // Sync the linked rankingCompetitions document (if one exists)
        if (form.startDate) {
          const startDateObj = new Date(form.startDate);
          const linkedRanking = await getRankingCompetitionByLinkedId(editingCompId);
          if (linkedRanking?.id) {
            await updateRankingCompetition(linkedRanking.id, {
              name: form.name,
              type: form.rankingType || "custom",
              date: Timestamp.fromDate(startDateObj),
              year: startDateObj.getFullYear(),
              month: startDateObj.getMonth() + 1,
              totalPoints: Number(form.rankingTotalPoints),
              participationPoints: Number(form.rankingParticipationPoints),
              pointsPerWin: Number(form.rankingPointsPerWin),
            });
          }
        }

        // Detect propagatable changes
        if (originalCompData) {
          const oldName: string = originalCompData.name ?? "";
          const newName: string = form.name;
          const oldStartDateStr: string = tsToDateStr(originalCompData.startDate);
          const newStartDateStr: string = form.startDate;

          const nameChanged = oldName !== newName;
          const dateChanged = oldStartDateStr !== newStartDateStr;

          if (nameChanged || dateChanged) {
            const propagateUpdates: { newName?: string; newStartDate?: Date } = {};
            if (nameChanged) propagateUpdates.newName = newName;

            let yearChanged = false;
            if (dateChanged && newStartDateStr) {
              const newDateObj = new Date(newStartDateStr);
              propagateUpdates.newStartDate = newDateObj;
              if (oldStartDateStr) {
                const oldYear = new Date(oldStartDateStr).getFullYear();
                yearChanged = newDateObj.getFullYear() !== oldYear;
              } else {
                yearChanged = false;
              }
            }

            setPropagationPending({
              competitionId: editingCompId,
              updates: propagateUpdates,
              yearChanged,
            });
            setPropagateResult(null);
            setPropagateError("");
          }
        }

        setEditingCompId(null);
      } else {
        // ── CREATE new competition ───────────────────────────────────────────
        const docRef = await addCompetition(compPayload, createImageFile ?? undefined);

        if (form.startDate) {
          const startDateObj = new Date(form.startDate);
          await addRankingCompetition({
            name: form.name,
            nameKa: form.name,
            type: form.rankingType || "custom",
            date: Timestamp.fromDate(startDateObj),
            year: startDateObj.getFullYear(),
            month: startDateObj.getMonth() + 1,
            location: "",
            totalPoints: Number(form.rankingTotalPoints),
            participationPoints: Number(form.rankingParticipationPoints),
            pointsPerWin: Number(form.rankingPointsPerWin),
            isActive: true,
            linkedCompetitionId: docRef.id,
          });
        }
      }
      setForm(EMPTY_FORM);
      setSelectedTypes([]);
      setCreateImageFile(null);
      setCreateImagePreview("");
      setMaleWeights([]);
      setFemaleWeights([]);
      setShowForm(false);
      setOriginalCompData(null);
      await loadCompetitions();
    } catch {
      setError(t("admin.competitions.error.save"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCompId(null);
    setForm(EMPTY_FORM);
    setSelectedTypes([]);
    setCreateImageFile(null);
    setCreateImagePreview("");
    setMaleWeights([]);
    setFemaleWeights([]);
    setError("");
    setOriginalCompData(null);
  };

  // ---------------------------------------------------------------------------
  // Propagation handler
  // ---------------------------------------------------------------------------

  const handlePropagate = async () => {
    if (!propagatePending) return;

    // If year changed, show a strong confirmation warning first
    if (propagatePending.yearChanged) {
      const confirmed = window.confirm(t("admin.competitions.propagate.yearChangeWarning"));
      if (!confirmed) return;
    }

    setPropagating(true);
    setPropagateError("");
    try {
      const result = await propagateCompetitionRename(
        propagatePending.competitionId,
        propagatePending.updates
      );
      setPropagateResult(result);
      setPropagationPending(null);
    } catch (err) {
      setPropagateError(t("admin.competitions.propagate.error"));
    } finally {
      setPropagating(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Status toggle
  // ---------------------------------------------------------------------------

  const handleToggleStatus = async (comp: any) => {
    const newStatus = comp.status === "open" ? "closed" : "open";
    if (!window.confirm(`Set "${comp.name}" to ${newStatus}?`)) return;
    try {
      await updateCompetitionStatus(comp.id, newStatus);
      await loadCompetitions();
    } catch {
      setError(t("admin.competitions.error.update"));
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------

  const handleDeleteClick = async (comp: any) => {
    // Count comes from the denormalized comp.registrationCount field, kept in
    // sync by the maintainCompetitionCount Cloud Function. No extra read needed.
    setDeleteModal({
      open: true,
      comp,
      regCount: Number(comp.registrationCount ?? 0),
      checking: false,
      deleting: false,
    });
  };



  const handleConfirmDelete = async () => {
    if (!deleteModal.comp) return;
    setDeleteModal((prev) => ({ ...prev, deleting: true }));
    try {
      await deleteCompetition(
        deleteModal.comp.id,
        deleteModal.comp.imageUrl,
        deleteModal.comp.documentUrl,
        deleteModal.comp.documentUrl2
      );
      setDeleteModal({ open: false, comp: null, regCount: 0, checking: false, deleting: false });
      await loadCompetitions();
    } catch {
      setError(t("admin.competitions.error.delete"));
      setDeleteModal((prev) => ({ ...prev, deleting: false }));
    }
  };

  // ---------------------------------------------------------------------------
  // Image upload (for existing competitions)
  // ---------------------------------------------------------------------------

  const handleImageUploadClick = (compId: string) => {
    setUploadingForComp(compId);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
      imageInputRef.current.click();
    }
  };

  const handleImageFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingForComp) return;
    setUploadingImage(uploadingForComp);
    try {
      const comp = competitions.find((c) => c.id === uploadingForComp);
      await updateCompetitionImage(uploadingForComp, file, comp?.imageUrl || "");
      await loadCompetitions();
    } catch {
      setError("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(null);
      setUploadingForComp(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Official document (PDF) upload / remove — for existing competitions.
  // Intentionally NOT gated behind isRankingAdmin: ranking_admin manages the
  // official provision/regulations PDF too (but still sees no money data).
  // ---------------------------------------------------------------------------

  const handleDocUploadClick = (compId: string) => {
    setDocForComp(compId);
    if (docInputRef.current) {
      docInputRef.current.value = "";
      docInputRef.current.click();
    }
  };

  const handleDocFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file || !docForComp) return;
    // PDFs only — guard against an accidental wrong-type pick.
    const isPdf =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setError(t("admin.competitions.doc.pdfOnly"));
      setDocForComp(null);
      return;
    }
    setUploadingDoc(docForComp);
    try {
      const comp = competitions.find((c) => c.id === docForComp);
      await updateCompetitionDocument(docForComp, file, comp?.documentUrl || "");
      await loadCompetitions();
    } catch {
      setError(t("admin.competitions.doc.uploadFailed"));
    } finally {
      setUploadingDoc(null);
      setDocForComp(null);
    }
  };

  const handleRemoveDoc = async (comp: any) => {
    if (!comp?.documentUrl) return;
    setUploadingDoc(comp.id);
    try {
      await removeCompetitionDocument(comp.id, comp.documentUrl);
      await loadCompetitions();
    } catch {
      setError(t("admin.competitions.doc.removeFailed"));
    } finally {
      setUploadingDoc(null);
    }
  };

  // ── Second optional document (e.g. English version) ──────────────────────
  const handleDoc2UploadClick = (compId: string) => {
    setDoc2ForComp(compId);
    if (doc2InputRef.current) {
      doc2InputRef.current.value = "";
      doc2InputRef.current.click();
    }
  };

  const handleDoc2FileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file || !doc2ForComp) return;
    // PDFs only — guard against an accidental wrong-type pick.
    const isPdf =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setError(t("admin.competitions.doc.pdfOnly"));
      setDoc2ForComp(null);
      return;
    }
    setUploadingDoc2(doc2ForComp);
    try {
      const comp = competitions.find((c) => c.id === doc2ForComp);
      await updateCompetitionDocument2(doc2ForComp, file, comp?.documentUrl2 || "");
      await loadCompetitions();
    } catch {
      setError(t("admin.competitions.doc.uploadFailed"));
    } finally {
      setUploadingDoc2(null);
      setDoc2ForComp(null);
    }
  };

  const handleRemoveDoc2 = async (comp: any) => {
    if (!comp?.documentUrl2) return;
    setUploadingDoc2(comp.id);
    try {
      await removeCompetitionDocument2(comp.id, comp.documentUrl2);
      await loadCompetitions();
    } catch {
      setError(t("admin.competitions.doc.removeFailed"));
    } finally {
      setUploadingDoc2(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Details toggle
  // ---------------------------------------------------------------------------

  const toggleDetails = (compId: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(compId)) next.delete(compId);
      else next.add(compId);
      return next;
    });
  };

  // ---------------------------------------------------------------------------
  // Link competition to an existing rankingCompetitions document
  //
  // Finds a matching rankingCompetitions record by name + year and writes
  // linkedCompetitionId onto it. Never creates a new ranking competition.
  // Requires an exact single match; shows a descriptive error otherwise.
  // ---------------------------------------------------------------------------

  const handleLinkToRankings = async (comp: any) => {
    const year: number = comp.startDate?.toDate
      ? comp.startDate.toDate().getFullYear()
      : new Date(comp.startDate).getFullYear();

    setLinkingCompId(comp.id);
    // Clear previous feedback for this card while the operation is running
    setLinkFeedback((prev) => ({ ...prev, [comp.id]: {} }));

    try {
      const allRankingComps = await getRankingCompetitions();

      const matches = allRankingComps.filter(
        (rc) => rc.name === comp.name && rc.year === year
      );

      if (matches.length === 0) {
        setLinkFeedback((prev) => ({
          ...prev,
          [comp.id]: {
            error:
              `No ranking competition found matching name "${comp.name}" and year ${year}. ` +
              `Create one manually in Manage Rankings first, or check for name typos.`,
          },
        }));
        return;
      }

      if (matches.length > 1) {
        setLinkFeedback((prev) => ({
          ...prev,
          [comp.id]: {
            error:
              `${matches.length} duplicate ranking competitions match "${comp.name}" (${year}). ` +
              `Delete the duplicates in Manage Rankings, then try again.`,
          },
        }));
        return;
      }

      // Exactly one match — update it with the linkedCompetitionId
      const match = matches[0];
      await updateRankingCompetition(match.id!, { linkedCompetitionId: comp.id });

      setLinkFeedback((prev) => ({
        ...prev,
        [comp.id]: {
          success: `✓ Linked to ranking competition "${match.name}" (${match.year}).`,
        },
      }));
    } catch {
      setLinkFeedback((prev) => ({
        ...prev,
        [comp.id]: { error: "Failed to link. Please try again." },
      }));
    } finally {
      setLinkingCompId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const formatDate = (timestamp: any, localeStr?: string): string => {
    if (!timestamp) return "—";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (localeStr === "ka") return formatDateGeorgian(date);
    return date.toLocaleDateString("en-GB");
  };

  const inputStyle = (field: string): React.CSSProperties => ({
    ...s.input,
    ...(focusedField === field ? s.inputFocused : {}),
  });

  let filteredCompetitions = competitions.filter((c) =>
    activeTab === "open" ? c.status === "open" : c.status === "closed"
  );
  if (searchText.trim()) {
    const q = searchText.toLowerCase();
    filteredCompetitions = filteredCompetitions.filter((c) =>
      c.name.toLowerCase().includes(q)
    );
  }

  // ── Pagination for Past/Closed tab ────────────────────────────────────────
  const PAST_PAGE_SIZE = 10;
  const pastTotalPages = activeTab === "closed" ? Math.ceil(filteredCompetitions.length / PAST_PAGE_SIZE) : 1;
  const pagedCompetitions = activeTab === "closed"
    ? filteredCompetitions.slice((pastPage - 1) * PAST_PAGE_SIZE, pastPage * PAST_PAGE_SIZE)
    : filteredCompetitions;

  // Reset page when tab or search changes
  useEffect(() => { setPastPage(1); }, [activeTab]);
  useEffect(() => { setPastPage(1); }, [searchText]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div style={s.page} className="page-content">
      <Navbar />

      {/* Hidden image file input for existing competitions */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImageFileChange}
      />

      {/* Hidden PDF input for the official competition document */}
      <input
        ref={docInputRef}
        type="file"
        accept="application/pdf,.pdf"
        style={{ display: "none" }}
        onChange={handleDocFileChange}
      />

      {/* Hidden PDF input for the SECOND optional competition document */}
      <input
        ref={doc2InputRef}
        type="file"
        accept="application/pdf,.pdf"
        style={{ display: "none" }}
        onChange={handleDoc2FileChange}
      />

      {/* Delete modal */}
      {deleteModal.open && deleteModal.comp && (
        <DeleteModal
          comp={deleteModal.comp}
          regCount={deleteModal.regCount}
          checking={deleteModal.checking}
          deleting={deleteModal.deleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            if (!deleteModal.deleting)
              setDeleteModal({
                open: false,
                comp: null,
                regCount: 0,
                checking: false,
                deleting: false,
              });
          }}
          t={t}
        />
      )}

      <div style={{ ...s.container, padding: isMobile ? "20px 16px" : "40px 24px" }}>

        {/* Page header */}
        <div style={{ ...s.header, flexDirection: isMobile ? "column" : "row", gap: isMobile ? "12px" : undefined, alignItems: isMobile ? "flex-start" : "flex-start" }}>
          <div>
            <div style={s.pageLabel}>{t("admin.competitions.pageLabel")}</div>
            <div style={{ ...s.title, fontSize: isMobile ? "26px" : "36px" }}>{t("admin.competitions.title")}</div>
            <div style={s.subtitle}>
              {competitions.length} {t("admin.competitions.subtitle")}
            </div>
          </div>
          {!showForm && !isRankingAdmin && (
            <button style={s.primaryBtn} onClick={() => setShowForm(true)}>
              {t("admin.competitions.newBtn")}
            </button>
          )}
        </div>

        {/* Error banner */}
        {error && <div style={s.errorMsg}>{error}</div>}
        {isRankingAdmin && <div style={s.readOnlyNotice}>{t("admin.competitions.rankingReadOnly")}</div>}

        {/* ── Propagation pending banner ── */}
        {propagatePending && (
          <div style={{
            ...s.propagateBanner,
            ...(propagatePending.yearChanged
              ? { backgroundColor: C.redDim, borderColor: "rgba(218,41,28,0.4)" }
              : {}),
          }}>
            <div style={{
              fontSize: "13px",
              color: propagatePending.yearChanged ? C.red : "#856404",
              lineHeight: 1.5,
              fontWeight: 600,
            }}>
              {propagatePending.yearChanged ? "⛔" : "⚠️"}{" "}
              {propagatePending.updates.newName && (
                <>Name changed to <strong>"{propagatePending.updates.newName}"</strong>. </>
              )}
              {propagatePending.updates.newStartDate && (
                <>Start date changed. </>
              )}
              {propagatePending.yearChanged && (
                <span style={{ color: C.red }}>
                  Year change detected — this will reorganize ranking standings.
                </span>
              )}
            </div>
            <div style={{ fontSize: "13px", color: propagatePending.yearChanged ? C.red : "#856404" }}>
              {t("admin.competitions.propagate.pending")}
            </div>
            {propagateError && (
              <div style={s.errorMsg}>{propagateError}</div>
            )}
            <button
              style={{
                ...s.propagateBtn,
                ...(propagatePending.yearChanged ? { backgroundColor: C.red } : {}),
              }}
              onClick={handlePropagate}
              disabled={propagating}
            >
              {propagating
                ? t("admin.competitions.propagate.propagating")
                : t("admin.competitions.propagate.button")}
            </button>
            <div style={{ fontSize: "11px", color: propagatePending.yearChanged ? C.red : "var(--color-warning)" }}>
              {t("admin.competitions.propagate.republishReminder")}
            </div>
          </div>
        )}

        {/* ── Propagation success message ── */}
        {propagateResult && (
          <div style={s.propagateSuccess}>
            ✓{" "}
            {t("admin.competitions.propagate.success")
              .replace("{rr}", String(propagateResult.rankingResultsUpdated))
              .replace("{reg}", String(propagateResult.registrationsUpdated))}
            {propagateResult.rankingCompetitionUpdated && " Rankings competition record updated."}
            <br />
            <span style={{ fontSize: "12px", opacity: 0.85 }}>
              {t("admin.competitions.propagate.republishReminder")}
            </span>
          </div>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* Create / Edit form                                                 */}
        {/* ----------------------------------------------------------------- */}
        {showForm && (
          <div style={s.formCard}>
            <div style={s.formCardAccent} />
            <div style={s.formCardBody}>
              <div style={s.formTitle}>
                {editingCompId
                  ? `✏️ Edit Competition`
                  : t("admin.competitions.form.title")}
              </div>
              <form onSubmit={handleSubmit} style={s.form}>

                {/* Basic Info */}
                <div style={s.section}>
                  <div style={s.sectionLabel}>{t("admin.competitions.form.basicInfo")}</div>
                  <div style={s.grid2}>
                    <div style={s.field}>
                      <label style={s.label}>{t("admin.competitions.name")}</label>
                      <input
                        name="name"
                        value={form.name}
                        onChange={handleFormChange}
                        placeholder="e.g. National Championship 2025"
                        required
                        style={inputStyle("name")}
                        onFocus={() => setFocusedField("name")}
                        onBlur={() => setFocusedField("")}
                      />
                    </div>

                    <div style={s.field}>
                      <label style={s.label}>{t("admin.competitions.types")}</label>
                      <div style={s.checkboxGroup}>
                        {TYPE_OPTIONS.map((opt) => (
                          <label key={opt} style={s.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={selectedTypes.includes(opt)}
                              onChange={() => handleTypeToggle(opt)}
                              style={s.checkbox}
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                      {selectedTypes.length > 0 && (
                        <div style={s.selectedTypesPreview}>
                          {selectedTypes.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Image upload during creation */}
                <div style={s.section}>
                  <div style={s.sectionLabel}>{t("admin.competitions.image.upload")}</div>
                  <div style={s.imageUploadRow}>
                    <input
                      ref={createImageInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleCreateImageChange}
                    />
                    <button
                      type="button"
                      style={s.secondaryBtn}
                      onClick={() => createImageInputRef.current?.click()}
                    >
                      {createImageFile
                        ? t("admin.competitions.image.change")
                        : t("admin.competitions.image.choose")}
                    </button>
                    {createImageFile && (
                      <span style={s.imageFileName}>{createImageFile.name}</span>
                    )}
                  </div>
                  {createImagePreview && (
                    <div style={s.imagePreviewWrapper}>
                      <img src={createImagePreview} alt="Preview" style={s.imagePreview} />
                    </div>
                  )}
                </div>

                {/* Dates */}
                <div style={s.section}>
                  <div style={s.sectionLabel}>{t("admin.competitions.form.dates")}</div>
                  <div style={s.grid3}>
                    {[
                      { name: "startDate", label: t("admin.competitions.startDate") },
                      { name: "endDate", label: t("admin.competitions.endDate") },
                      { name: "registrationDeadline", label: t("admin.competitions.regDeadline") },
                      { name: "earlyDeadline", label: t("admin.competitions.earlyDeadline") },
                      { name: "midDeadline", label: t("admin.competitions.midDeadline") },
                      { name: "weightChangeDeadline", label: t("admin.competitions.weightChangeDeadline"), required: false },
                    ].map(({ name, label, required }: any) => (
                      <div key={name} style={s.field}>
                        <label style={s.label}>
                          {label}
                          {required === false && (
                            <span style={{ color: C.text3, fontWeight: 400, marginLeft: 6 }}>
                              ({t("common.optional") || "optional"})
                            </span>
                          )}
                        </label>
                        <input
                          name={name}
                          type="date"
                          value={(form as any)[name]}
                          onChange={handleFormChange}
                          required={required !== false}
                          style={inputStyle(name)}
                          onFocus={() => setFocusedField(name)}
                          onBlur={() => setFocusedField("")}
                        />
                        {name === "weightChangeDeadline" && (
                          <>
                            <div style={{ fontSize: 11, color: C.text3, marginTop: 4 }}>
                              {t("admin.competitions.weightChangeDeadlineHint")}
                            </div>
                            <div style={{ fontSize: 11, color: C.warning, marginTop: 4, fontWeight: 600, lineHeight: 1.4 }}>
                              ⚠ {t("admin.competitions.weightChangeDeadlineWarn")}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing */}
                <div style={s.section}>
                  <div style={s.sectionLabel}>{t("admin.competitions.form.pricing")}</div>
                  <div style={s.grid3}>
                    {[
                      { name: "earlyPrice", label: t("admin.competitions.earlyPrice") },
                      { name: "midPrice", label: t("admin.competitions.midPrice") },
                      { name: "latePrice", label: t("admin.competitions.latePrice") },
                    ].map(({ name, label }) => (
                      <div key={name} style={s.field}>
                        <label style={s.label}>{label}</label>
                        <input
                          name={name}
                          type="number"
                          min="0"
                          value={(form as any)[name]}
                          onChange={handleFormChange}
                          placeholder={t("admin.competitions.labels.gel")}
                          required
                          style={inputStyle(name)}
                          onFocus={() => setFocusedField(name)}
                          onBlur={() => setFocusedField("")}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ranking Points */}
                <div style={s.section}>
                  <div style={s.sectionLabel}>Ranking Points (optional)</div>
                  <div style={s.grid3}>
                    <div style={s.field}>
                      <label style={s.label}>Competition Type</label>
                      <select
                        style={inputStyle("rankingType")}
                        name="rankingType"
                        value={form.rankingType}
                        onFocus={() => setFocusedField("rankingType")}
                        onBlur={() => setFocusedField("")}
                        onChange={(e) => {
                          const type = e.target.value;
                          const defaults = getDefaultPointsForType(type);
                          setForm((prev) => ({
                            ...prev,
                            rankingType: type,
                            rankingTotalPoints: defaults.totalPoints,
                            rankingParticipationPoints: defaults.participationPoints,
                            rankingPointsPerWin: defaults.pointsPerWin,
                          }));
                        }}
                      >
                        {Object.entries(COMPETITION_TYPE_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Max Points (1st)</label>
                      <input
                        style={inputStyle("rankingTotalPoints")}
                        name="rankingTotalPoints"
                        type="number"
                        min="0"
                        value={form.rankingTotalPoints}
                        onChange={handleFormChange}
                        onFocus={() => setFocusedField("rankingTotalPoints")}
                        onBlur={() => setFocusedField("")}
                      />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Participation Points</label>
                      <input
                        style={inputStyle("rankingParticipationPoints")}
                        name="rankingParticipationPoints"
                        type="number"
                        min="0"
                        value={form.rankingParticipationPoints}
                        onChange={handleFormChange}
                        onFocus={() => setFocusedField("rankingParticipationPoints")}
                        onBlur={() => setFocusedField("")}
                      />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Points per Win</label>
                      <input
                        style={inputStyle("rankingPointsPerWin")}
                        name="rankingPointsPerWin"
                        type="number"
                        min="0"
                        value={form.rankingPointsPerWin}
                        onChange={handleFormChange}
                        onFocus={() => setFocusedField("rankingPointsPerWin")}
                        onBlur={() => setFocusedField("")}
                      />
                    </div>
                  </div>
                </div>

                {/* Weight Categories */}
                <div style={s.section}>
                  <div style={s.sectionLabel}>{t("admin.competitions.form.weightCategories")}</div>

                  {selectedTypes.length > 0 && (
                    <div style={{ marginBottom: "12px" }}>
                      {!isRankingAdmin && <button
                        type="button"
                        style={s.autoWeightBtn}
                        onClick={loadDefaultWeights}
                      >
                        ↺ {t("admin.competitions.loadDefaultWeights.button")}: {selectedTypes.join(", ")}
                      </button>}
                      <div style={s.autoWeightNote}>
                        {t("admin.competitions.loadDefaultWeights.note")}
                      </div>
                    </div>
                  )}

                  <div style={s.grid2}>
                    {/* Male */}
                    <div>
                      <div style={s.label}>{t("admin.competitions.maleCategories")}</div>
                      <div style={s.weightInputRow}>
                        <input
                          value={maleInput}
                          onChange={(e) => setMaleInput(e.target.value)}
                          onKeyDown={(e) =>
                            e.key === "Enter" && (e.preventDefault(), addWeight("male"))
                          }
                          placeholder="-58kg"
                          style={{ ...inputStyle("maleInput"), flex: 1 }}
                          onFocus={() => setFocusedField("maleInput")}
                          onBlur={() => setFocusedField("")}
                        />
                        <button
                          type="button"
                          style={s.addWeightBtn}
                          onClick={() => addWeight("male")}
                        >
                          {t("admin.competitions.addWeight")}
                        </button>
                      </div>
                      <div style={s.tagRow}>
                        {maleWeights.map((w) => (
                          <span key={w} style={s.tag}>
                            {w}
                            <button
                              type="button"
                              style={s.tagRemove}
                              onClick={() => removeWeight("male", w)}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Female */}
                    <div>
                      <div style={s.label}>{t("admin.competitions.femaleCategories")}</div>
                      <div style={s.weightInputRow}>
                        <input
                          value={femaleInput}
                          onChange={(e) => setFemaleInput(e.target.value)}
                          onKeyDown={(e) =>
                            e.key === "Enter" && (e.preventDefault(), addWeight("female"))
                          }
                          placeholder="-53kg"
                          style={{ ...inputStyle("femaleInput"), flex: 1 }}
                          onFocus={() => setFocusedField("femaleInput")}
                          onBlur={() => setFocusedField("")}
                        />
                        <button
                          type="button"
                          style={s.addWeightBtn}
                          onClick={() => addWeight("female")}
                        >
                          {t("admin.competitions.addWeight")}
                        </button>
                      </div>
                      <div style={s.tagRow}>
                        {femaleWeights.map((w) => (
                          <span key={w} style={s.tag}>
                            {w}
                            <button
                              type="button"
                              style={s.tagRemove}
                              onClick={() => removeWeight("female", w)}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form actions */}
                <div style={s.formActions}>
                  <button type="submit" style={s.primaryBtn} disabled={saving}>
                    {saving
                      ? "Saving..."
                      : editingCompId
                      ? "Save Changes"
                      : t("admin.competitions.createBtn")}
                  </button>
                  <button type="button" style={s.secondaryBtn} onClick={handleCancel}>
                    {t("admin.competitions.cancel")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* Tabs                                                               */}
        {/* ----------------------------------------------------------------- */}
        <div style={s.tabBar}>
          {(["open", "closed"] as const).map((tab) => (
            <button
              key={tab}
              style={activeTab === tab ? s.tabActive : s.tabInactive}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "open"
                ? t("admin.competitions.tab.open")
                : t("admin.competitions.tab.past")}
              <span style={activeTab === tab ? s.tabCountActive : s.tabCount}>
                {competitions.filter((c) =>
                  tab === "open" ? c.status === "open" : c.status === "closed"
                ).length}
              </span>
            </button>
          ))}
        </div>


        {/* Search bar */}
        {!loading && (
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder={t("admin.allRegistrations.search")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px",
                fontSize: 14,
                borderRadius: 8,
                border: `1px solid ${C.border2}`,
                backgroundColor: C.surface2,
                color: C.text,
                outline: "none",
                fontFamily: FONT.body,
                boxSizing: "border-box",
              }}
            />
          </div>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* Competition list                                                   */}
        {/* ----------------------------------------------------------------- */}
        {loading && (
          <div style={s.stateMsg}>{t("admin.competitions.loading")}</div>
        )}
        {!loading && filteredCompetitions.length === 0 && (
          <div style={s.emptyBox}>
            <div style={s.emptyIcon}>🏆</div>
            <div style={s.emptyText}>
              {activeTab === "open"
                ? t("admin.competitions.empty")
                : t("admin.competitions.past.empty")}
            </div>
          </div>
        )}

        {!loading && filteredCompetitions.length > 0 && (
          <div style={{ ...s.compGrid, gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)" }}>
            {pagedCompetitions.map((comp: any) => {
              const isExpanded = expandedCards.has(comp.id);
              const typeList: string[] = comp.types ?? (comp.type ? [comp.type] : []);
              const feedback = linkFeedback[comp.id];

              return (
                <div key={comp.id} style={s.deskCard}>
                  {/* Left side – image / placeholder */}
                  {comp.imageUrl ? (
                    <div style={{ ...s.deskCardImage, backgroundImage: `url(${comp.imageUrl})` }}>
                    </div>
                  ) : (
                    <div style={s.deskCardPlaceholder}>
                      <div style={{ color: "rgba(0,0,0,0.25)" }}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="3" y="4" width="18" height="17" rx="2" ry="2" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* Right side – all content */}
                  <div style={s.deskCardBody}>
                    <div style={s.deskCardHeader}>
                      <div style={s.deskCompName}>{comp.name}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                        {/* Registration count badge — denormalized field on
                            the competition doc, maintained by the
                            maintainCompetitionCount Cloud Function. */}
                        <span
                          title="Registered athletes"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            backgroundColor: (comp.registrationCount ?? 0) > 0 ? C.redDim : C.surface2,
                            color: (comp.registrationCount ?? 0) > 0 ? C.red : C.text3,
                            border: `1px solid ${(comp.registrationCount ?? 0) > 0 ? C.red : C.border}`,
                            borderRadius: "20px",
                            padding: "3px 10px",
                            fontSize: "11px",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          📋 {comp.registrationCount ?? 0}
                        </span>
                        <div
                          style={{
                            ...s.statusBadge,
                            backgroundColor:
                              comp.status === "open" ? C.successDim : C.surface2,
                            color: comp.status === "open" ? C.success : C.text3,
                            border: `1px solid ${
                              comp.status === "open" ? C.success : C.border
                            }`,
                          }}
                        >
                          {comp.status === "open"
                            ? t("admin.competitions.status.open")
                            : t("admin.competitions.status.closed")}
                        </div>
                      </div>
                    </div>

                    <div style={s.deskTypeBadgeRow}>
                      {typeList.map((type: string) => (
                        <span key={type} style={s.deskTypeBadge}>{type}</span>
                      ))}
                    </div>

                    <div style={s.deskMetaBlock}>
                      <div style={s.deskMetaLine}>
                        <span style={s.deskMetaIcon}>📅</span>
                        <span style={s.deskMetaKey}>{t("admin.competitions.labels.start")}:</span>
                        <span style={s.deskMetaVal}>{formatDate(comp.startDate, locale)}</span>
                      </div>
                      <div style={s.deskMetaLine}>
                        <span style={s.deskMetaIcon}>📅</span>
                        <span style={s.deskMetaKey}>{t("admin.competitions.labels.end")}:</span>
                        <span style={s.deskMetaVal}>{formatDate(comp.endDate, locale)}</span>
                      </div>
                      <div style={s.deskMetaLine}>
                        <span style={s.deskMetaIcon}>⏰</span>
                        <span style={s.deskMetaKey}>{t("admin.competitions.labels.deadline")}:</span>
                        <span style={s.deskMetaVal}>{formatDate(comp.registrationDeadline, locale)}</span>
                      </div>
                    </div>

                    <button
                      style={s.deskDetailsToggle}
                      onClick={() => toggleDetails(comp.id)}
                      type="button"
                    >
                      {isExpanded
                        ? t("admin.competitions.hideDetails")
                        : t("admin.competitions.showDetails")}
                    </button>

                    {isExpanded && (
                      <div style={s.deskExpandedSection}>
                        <div style={s.deskMetaBlock}>
                          <div style={s.deskMetaLine}>
                            <span style={s.deskMetaIcon}>💰</span>
                            <span style={s.deskMetaKey}>{t("admin.competitions.labels.early")}:</span>
                            <span style={s.deskMetaVal}>
                              {comp.earlyPrice} {t("admin.competitions.labels.gel")} (
                              {t("admin.competitions.labels.until")} {formatDate(comp.earlyDeadline, locale)})
                            </span>
                          </div>
                          <div style={s.deskMetaLine}>
                            <span style={s.deskMetaIcon}>💰</span>
                            <span style={s.deskMetaKey}>{t("admin.competitions.labels.mid")}:</span>
                            <span style={s.deskMetaVal}>
                              {comp.midPrice} {t("admin.competitions.labels.gel")} (
                              {t("admin.competitions.labels.until")} {formatDate(comp.midDeadline, locale)})
                            </span>
                          </div>
                          <div style={s.deskMetaLine}>
                            <span style={s.deskMetaIcon}>💰</span>
                            <span style={s.deskMetaKey}>{t("admin.competitions.labels.late")}:</span>
                            <span style={s.deskMetaVal}>
                              {comp.latePrice} {t("admin.competitions.labels.gel")}
                            </span>
                          </div>
                        </div>

                        <div style={s.deskWeightGrid}>
                          {comp.weightCategories?.male?.length > 0 && (
                            <div>
                              <div style={s.deskWeightLabel}>{t("admin.competitions.labels.male")}</div>
                              <div style={s.deskWeightTags}>
                                {comp.weightCategories.male.map((w: string) => (
                                  <span key={w} style={s.deskWeightTag}>{w}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {comp.weightCategories?.female?.length > 0 && (
                            <div>
                              <div style={s.deskWeightLabel}>{t("admin.competitions.labels.female")}</div>
                              <div style={s.deskWeightTags}>
                                {comp.weightCategories.female.map((w: string) => (
                                  <span key={w} style={s.deskWeightTag}>{w}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div style={s.deskCompActions}>
                      {!isRankingAdmin && <><button
                        style={{
                          ...s.deskSecondaryBtn,
                          color: C.blue,
                          borderColor: C.blue,
                          fontWeight: 700,
                        }}
                        onClick={() => handleEditClick(comp)}
                      >
                        ✏️ Edit
                      </button>

                      {!isRankingAdmin && (
                        <button
                          style={s.deskSecondaryBtn}
                          onClick={() => handleToggleStatus(comp)}
                        >
                          {comp.status === "open"
                            ? t("admin.competitions.closeReg")
                            : t("admin.competitions.reopenReg")}
                        </button>
                      )}

                      {/* View Registrations targets an admin-only route
                          (/admin/registrations/:competitionId). Hiding for
                          ranking_admin avoids a dead-click bounce to /admin. */}
                      {!isRankingAdmin && (
                        <button
                          style={s.deskSecondaryBtn}
                          onClick={() =>
                            navigate(`/admin/registrations/${comp.id}`, {
                              state: {
                                competitionName: comp.name,
                                competitionTypes: typeList,
                                weightCategories: comp.weightCategories ?? { male: [], female: [] },
                              },
                            })
                          }
                        >
                          {t("admin.competitions.viewRegs")}
                        </button>
                      )}

                      <button
                        style={s.deskSecondaryBtn}
                        onClick={() => handleImageUploadClick(comp.id)}
                        disabled={uploadingImage === comp.id}
                      >
                        {uploadingImage === comp.id
                          ? t("admin.competitions.image.uploading")
                          : comp.imageUrl
                          ? t("admin.competitions.image.change")
                          : t("admin.competitions.image.upload")}
                      </button>

                      {/* Official document (PDF) — available to admin AND
                          ranking_admin. Upload/replace + remove. */}
                      <button
                        style={s.deskSecondaryBtn}
                        onClick={() => handleDocUploadClick(comp.id)}
                        disabled={uploadingDoc === comp.id}
                      >
                        {uploadingDoc === comp.id
                          ? t("admin.competitions.doc.uploading")
                          : comp.documentUrl
                          ? `📄 ${t("admin.competitions.doc.replace")}`
                          : `📄 ${t("admin.competitions.doc.upload")}`}
                      </button>
                      {comp.documentUrl && (
                        <button
                          style={s.deskSecondaryBtn}
                          onClick={() => handleRemoveDoc(comp)}
                          disabled={uploadingDoc === comp.id}
                        >
                          {t("admin.competitions.doc.remove")}
                        </button>
                      )}

                      {/* Second optional document (e.g. English version) */}
                      <button
                        style={s.deskSecondaryBtn}
                        onClick={() => handleDoc2UploadClick(comp.id)}
                        disabled={uploadingDoc2 === comp.id}
                      >
                        {uploadingDoc2 === comp.id
                          ? t("admin.competitions.doc2.uploading")
                          : comp.documentUrl2
                          ? `📄 ${t("admin.competitions.doc2.replace")}`
                          : `📄 ${t("admin.competitions.doc2.upload")}`}
                      </button>
                      {comp.documentUrl2 && (
                        <button
                          style={s.deskSecondaryBtn}
                          onClick={() => handleRemoveDoc2(comp)}
                          disabled={uploadingDoc2 === comp.id}
                        >
                          {t("admin.competitions.doc2.remove")}
                        </button>
                      )}
                      </>}

                      {!isRankingAdmin && (
                        <button
                          style={s.deskManualRegBtn}
                          onClick={() => setManualRegComp(comp)}
                        >
                          + {t("admin.competitions.manualReg")}
                        </button>
                      )}

                      <button
                        style={s.deskLinkBtn}
                        onClick={() => handleLinkToRankings(comp)}
                        disabled={linkingCompId === comp.id}
                        type="button"
                      >
                        {linkingCompId === comp.id ? "Linking..." : "🔗 Link to rankings"}
                      </button>

                      {!isRankingAdmin && (
                        <button
                          style={s.deskDeleteBtn}
                          onClick={() => handleDeleteClick(comp)}
                        >
                          {t("admin.competitions.deleteBtn")}
                        </button>
                      )}
                    </div>

                    {/* Inline feedback for the link operation */}
                    {feedback?.success && (
                      <div style={s.deskLinkSuccess}>{feedback.success}</div>
                    )}
                    {feedback?.error && (
                      <div style={s.deskLinkError}>{feedback.error}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Past tab pagination ── */}
        {!loading && activeTab === "closed" && pastTotalPages > 1 && (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 6,
            marginTop: 28,
            marginBottom: 12,
            flexWrap: "wrap",
          }}>
            {Array.from({ length: pastTotalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => { setPastPage(page); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                style={{
                  minWidth: 36,
                  height: 36,
                  padding: "0 10px",
                  borderRadius: 8,
                  border: page === pastPage
                    ? `1px solid ${C.red}`
                    : `1px solid ${C.border2}`,
                  backgroundColor: page === pastPage ? C.red : C.surface,
                  color: page === pastPage ? "#fff" : C.text2,
                  fontSize: 13,
                  fontWeight: page === pastPage ? 700 : 500,
                  fontFamily: FONT.body,
                  cursor: "pointer",
                  transition: "background 0.15s, color 0.15s, border-color 0.15s",
                }}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Manual Registration Modal */}
      {manualRegComp && (
        <ManualRegistrationModal
          competition={manualRegComp}
          onClose={() => setManualRegComp(null)}
          onSuccess={(refCode: string) => {
            setManualRegComp(null);
            alert(`Manual registration successful!\nReference: ${refCode}`);
            loadCompetitions();
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Delete modal styles
// ---------------------------------------------------------------------------

const dm: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.55)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  },
  modal: {
    backgroundColor: C.surface,
    borderRadius: "12px",
    width: "100%",
    maxWidth: "460px",
    overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  accentBar: { height: "4px", background: C.red },
  body: {
    padding: "28px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  title: { fontFamily: FONT.display, fontSize: "22px", color: C.text, letterSpacing: "0.5px" },
  compName: { fontSize: "15px", fontWeight: 700, color: C.text },
  msg: { fontSize: "13px", color: C.text2, lineHeight: 1.5 },
  checking: { fontSize: "13px", color: C.text3, fontStyle: "italic" },
  warning: {
    backgroundColor: C.warningDim,
    border: `1px solid ${C.warning}`,
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "13px",
    color: "#856404",
    lineHeight: 1.5,
  },
  actions: { display: "flex", gap: "10px", marginTop: "4px" },
  cancelBtn: {
    flex: 1,
    padding: "11px",
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  confirmBtn: {
    flex: 1,
    padding: "11px",
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  confirmBtnDisabled: {
    flex: 1,
    padding: "11px",
    backgroundColor: C.border2,
    color: C.text3,
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "not-allowed",
  },
};

// ---------------------------------------------------------------------------
// Page styles
// ---------------------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: C.bg, fontFamily: FONT.body },
  container: { maxWidth: "1200px", margin: "0 auto", display: "flex", flexDirection: "column", gap: "24px" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  pageLabel: { fontSize: "10px", fontWeight: 700, letterSpacing: "2px", color: C.red, textTransform: "uppercase", marginBottom: "4px" },
  title: { fontFamily: FONT.display, fontSize: "36px", color: C.text, letterSpacing: "1px", lineHeight: 1 },
  subtitle: { fontSize: "14px", color: C.text3, marginTop: "4px" },
  tabBar: { display: "flex", gap: "4px", borderBottom: `2px solid ${C.border}` },
  tabActive: {
    background: "none", border: "none", cursor: "pointer",
    padding: "10px 20px", fontSize: "13px", fontWeight: 700,
    color: C.red, borderBottom: `2px solid ${C.red}`,
    marginBottom: "-2px", display: "flex", alignItems: "center", gap: "8px", fontFamily: FONT.body,
  },
  tabInactive: {
    background: "none", border: "none", cursor: "pointer",
    padding: "10px 20px", fontSize: "13px", fontWeight: 500,
    color: C.text2, borderBottom: "2px solid transparent",
    marginBottom: "-2px", display: "flex", alignItems: "center", gap: "8px", fontFamily: FONT.body,
  },
  tabCount: {
    backgroundColor: C.surface2, color: C.text3, borderRadius: "20px",
    padding: "1px 8px", fontSize: "11px", fontWeight: 700,
  },
  tabCountActive: {
    backgroundColor: C.redDim, color: C.red, borderRadius: "20px",
    padding: "1px 8px", fontSize: "11px", fontWeight: 700,
  },
  formCard: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "12px",
    overflow: "hidden",
  },
  formCardAccent: { height: "4px", backgroundColor: C.red },
  formCardBody: { padding: "28px" },
  formTitle: { fontFamily: FONT.display, fontSize: "22px", color: C.text, letterSpacing: "0.5px", marginBottom: "24px" },
  form: { display: "flex", flexDirection: "column", gap: "24px" },
  section: { display: "flex", flexDirection: "column", gap: "12px" },
  sectionLabel: { fontSize: "11px", fontWeight: 700, letterSpacing: "1.2px", color: C.text2, textTransform: "uppercase" },
  grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" },
  grid3: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px" },
  field: { display: "flex", flexDirection: "column", gap: "6px" },
  label: { fontSize: "12px", fontWeight: 600, color: C.text2, letterSpacing: "0.3px" },
  input: {
    padding: "10px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    fontSize: "14px",
    outline: "none",
    color: C.text,
    backgroundColor: C.surface2,
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  inputFocused: { border: `1px solid ${C.red}`, boxShadow: `0 0 0 3px ${C.redDim}` },
  checkboxGroup: { display: "flex", flexWrap: "wrap", gap: "8px" },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    color: C.text,
    cursor: "pointer",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "6px 10px",
  },
  checkbox: { accentColor: C.red, cursor: "pointer" },
  selectedTypesPreview: { fontSize: "11px", color: C.text3, marginTop: "4px", fontStyle: "italic" },
  autoWeightBtn: {
    backgroundColor: C.blueDim,
    color: C.blue,
    border: `1px solid ${C.blue}`,
    borderRadius: "7px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-block",
  },
  autoWeightNote: { fontSize: "11px", color: C.text3, marginTop: "5px", fontStyle: "italic" },
  imageUploadRow: { display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" },
  imageFileName: { fontSize: "12px", color: C.text2 },
  imagePreviewWrapper: {
    marginTop: "10px",
    borderRadius: "8px",
    overflow: "hidden",
    width: "100%",
    maxWidth: "320px",
    height: "120px",
    border: `1px solid ${C.border}`,
  },
  imagePreview: { width: "100%", height: "100%", objectFit: "cover" },
  weightInputRow: { display: "flex", gap: "8px", marginTop: "6px", marginBottom: "8px" },
  addWeightBtn: {
    backgroundColor: C.surface2,
    color: C.text,
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    padding: "10px 14px",
    fontSize: "13px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontWeight: 600,
  },
  tagRow: { display: "flex", flexWrap: "wrap", gap: "6px" },
  tag: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border}`,
    borderRadius: "20px",
    padding: "4px 10px",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  tagRemove: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    color: C.text3,
    lineHeight: 1,
    padding: 0,
    fontWeight: 700,
  },
  formActions: { display: "flex", gap: "12px" },
  errorMsg: {
    backgroundColor: C.redDim,
    border: `1px solid rgba(218,41,28,0.3)`,
    borderRadius: "7px",
    padding: "10px 14px",
    fontSize: "13px",
    color: C.red,
    marginBottom: "16px",
  },
  readOnlyNotice: {
    marginBottom: "16px",
    border: `1px solid ${C.border}`,
    borderRadius: "8px",
    padding: "11px 14px",
    backgroundColor: C.surface,
    color: C.text2,
    fontSize: "12px",
    lineHeight: 1.5,
  },
  stateMsg: { color: C.text2, fontSize: "14px", padding: "40px 0", textAlign: "center" },
  emptyBox: {
    backgroundColor: C.surface,
    border: `1px dashed ${C.border2}`,
    borderRadius: "12px",
    padding: "60px",
    textAlign: "center",
  },
  emptyIcon: { fontSize: "40px", marginBottom: "16px" },
  emptyText: { color: C.text2, fontSize: "15px" },
  compGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "16px",
  },
  compCard: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "12px",
    overflow: "hidden",
  },
  compAccentBar: { height: "4px", backgroundColor: C.red },
  compImageRow: { width: "100%", height: "180px", overflow: "hidden" },
  compImage: { width: "100%", height: "100%", objectFit: "cover" },
  compCardBody: { padding: "18px 20px", display: "flex", flexDirection: "column", gap: "10px" },
  compTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" },
  compTopLeft: { display: "flex", alignItems: "center", gap: "8px" },
  compName: { fontFamily: FONT.body, fontSize: "15px", fontWeight: 700, color: C.text, lineHeight: 1.3 },
  typeBadgeRow: { display: "flex", flexWrap: "wrap", gap: "4px" },
  typeBadge: {
    backgroundColor: C.surface2,
    color: C.text2,
    borderRadius: "20px",
    padding: "3px 10px",
    fontSize: "11px",
    fontWeight: 600,
    border: `1px solid ${C.border}`,
  },
  statusBadge: {
    borderRadius: "20px",
    padding: "4px 12px",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  compDetails: { display: "flex", flexWrap: "wrap", gap: "10px" },
  compDetailItem: { display: "flex", gap: "4px", fontSize: "12px", color: C.text2 },
  compDetailLabel: { color: C.text3, fontWeight: 600 },
  detailsToggleBtn: {
    backgroundColor: "transparent",
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "6px 12px",
    fontSize: "12px",
    color: C.text2,
    cursor: "pointer",
    fontWeight: 600,
    alignSelf: "flex-start",
  },
  expandedSection: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    paddingTop: "10px",
    borderTop: `1px solid ${C.border}`,
  },
  weightGrid: { display: "flex", gap: "16px", flexWrap: "wrap" },
  weightSection: {},
  weightSectionLabel: {
    fontSize: "10px",
    color: C.text3,
    fontWeight: 700,
    letterSpacing: "0.5px",
    marginBottom: "6px",
    textTransform: "uppercase",
  },
  weightTags: { display: "flex", flexWrap: "wrap", gap: "4px" },
  weightTag: {
    backgroundColor: C.surface2,
    color: C.text2,
    borderRadius: "4px",
    padding: "2px 8px",
    fontSize: "11px",
    border: `1px solid ${C.border}`,
  },
  compActions: { display: "flex", gap: "8px", flexWrap: "wrap", paddingTop: "4px" },
  primaryBtn: {
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
  },
  secondaryBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    padding: "7px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  manualRegBtn: {
    backgroundColor: C.blue,
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    padding: "7px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  // Link to rankings button — neutral tone, distinct from manualRegBtn
  linkBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    padding: "7px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  deleteBtn: {
    backgroundColor: C.redDim,
    color: C.red,
    border: `1px solid rgba(218,41,28,0.3)`,
    borderRadius: "7px",
    padding: "7px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  // Inline feedback messages shown below the card actions after a link attempt
  linkSuccess: {
    fontSize: "12px",
    color: C.success,
    paddingTop: "4px",
    lineHeight: 1.4,
  },
  linkError: {
    fontSize: "12px",
    color: C.red,
    paddingTop: "4px",
    lineHeight: 1.4,
  },
  // Propagation styles
  propagateBanner: {
    backgroundColor: C.warningDim,
    border: `1px solid ${C.warning}`,
    borderRadius: "8px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  propagateBtn: {
    backgroundColor: C.warning,
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    alignSelf: "flex-start",
  },
  propagateSuccess: {
    backgroundColor: "var(--color-success-soft)",
    border: "1px solid #6ee7b7",
    borderRadius: "7px",
    padding: "12px 16px",
    fontSize: "13px",
    color: "var(--color-success)",
    fontWeight: 600,
    lineHeight: 1.6,
  },

  // ── New horizontal desktop card styles ─────────────────────────────────────
  deskCard: {
    display: "flex",
    flexDirection: "row" as const,
    backgroundColor: C.surface,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
    border: `1px solid ${C.border}`,
    transition: "transform 0.25s cubic-bezier(.22,1,.36,1), box-shadow 0.25s",
    maxWidth: 680,
    margin: "0 auto",
  },
  deskCardImage: {
    width: 130,
    flexShrink: 0,
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundColor: C.surface,
    position: "relative",
  },
  deskCardImageDim: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 100%)",
  },
  deskCardPlaceholder: {
    width: 130,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: C.surface2,
  },
  deskCardBody: {
    flex: 1,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column" as const,
    gap: 8,
  },
  deskCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
  },
  deskCompName: {
    fontFamily: FONT.body,
    fontSize: "16px",
    fontWeight: 700,
    color: C.text,
    lineHeight: 1.3,
  },
  deskTypeBadgeRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  },
  deskTypeBadge: {
    backgroundColor: C.surface2,
    color: C.text2,
    borderRadius: "20px",
    padding: "3px 10px",
    fontSize: "11px",
    fontWeight: 600,
    border: `1px solid ${C.border}`,
  },
  deskMetaBlock: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
    padding: "6px 0",
  },
  deskMetaLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: "12px",
    color: C.text2,
  },
  deskMetaIcon: {
    fontSize: "12px",
    flexShrink: 0,
  },
  deskMetaKey: {
    color: C.text3,
    fontWeight: 600,
    minWidth: "70px",
  },
  deskMetaVal: {
    color: C.text,
  },
  deskDetailsToggle: {
    backgroundColor: "transparent",
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "11px",
    color: C.text2,
    cursor: "pointer",
    fontWeight: 600,
    alignSelf: "flex-start",
  },
  deskExpandedSection: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
    paddingTop: 8,
    borderTop: `1px solid ${C.border}`,
  },
  deskWeightGrid: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap" as const,
  },
  deskWeightLabel: {
    fontSize: "10px",
    color: C.text3,
    fontWeight: 700,
    letterSpacing: "0.5px",
    marginBottom: 4,
    textTransform: "uppercase",
  },
  deskWeightTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  },
  deskWeightTag: {
    backgroundColor: C.surface2,
    color: C.text2,
    borderRadius: "4px",
    padding: "2px 8px",
    fontSize: "11px",
    border: `1px solid ${C.border}`,
  },
  deskCompActions: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
    paddingTop: "6px",
  },
  deskSecondaryBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "5px 10px",
    fontSize: "11px",
    cursor: "pointer",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  deskManualRegBtn: {
    backgroundColor: C.blue,
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "5px 10px",
    fontSize: "11px",
    cursor: "pointer",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  deskLinkBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "5px 10px",
    fontSize: "11px",
    cursor: "pointer",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  deskDeleteBtn: {
    backgroundColor: C.redDim,
    color: C.red,
    border: `1px solid rgba(218,41,28,0.3)`,
    borderRadius: "6px",
    padding: "5px 10px",
    fontSize: "11px",
    cursor: "pointer",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  deskLinkSuccess: {
    fontSize: "11px",
    color: C.success,
    paddingTop: "4px",
  },
  deskLinkError: {
    fontSize: "11px",
    color: C.red,
    paddingTop: "4px",
  },
};

export default ManageCompetitions;
